import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Download, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useSyncStore } from '@/store/syncStore';
import {
  discardAllQueueItems,
  listFailedQueueItems,
  listPendingQueueItems,
  queueStatsByType,
  requestSyncPause,
  runChunkedCatalogSync,
  syncNow,
} from '@/offline';
import type { SyncQueueItem } from '@/offline/db';
import toast from 'react-hot-toast';

export default function OfflineSyncPanel() {
  const qc = useQueryClient();
  const pending = useSyncStore((s) => s.pendingQueueCount);
  const failed = useSyncStore((s) => s.failedQueueCount);
  const isSyncing = useSyncStore((s) => s.isSyncing);
  const progress = useSyncStore((s) => s.syncProgress);
  const catalogSyncedAt = useSyncStore((s) => s.catalogSyncedAt);
  const catalogSyncOpen = useSyncStore((s) => s.catalogSyncProgress.open);
  const reachable = useSyncStore((s) => s.isServerReachable);
  const lastSyncError = useSyncStore((s) => s.lastSyncError);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [failedItems, setFailedItems] = useState<SyncQueueItem[]>([]);
  const [pendingItems, setPendingItems] = useState<SyncQueueItem[]>([]);
  const [bootstrapping, setBootstrapping] = useState(false);

  const refreshLists = () => {
    void listFailedQueueItems().then(setFailedItems);
    void listPendingQueueItems().then(setPendingItems);
    void queueStatsByType().then(setStats);
  };

  useEffect(() => {
    refreshLists();
    const t = setInterval(refreshLists, 3000);
    return () => clearInterval(t);
  }, [pending, failed, isSyncing]);

  const invalidateCatalogQueries = () => {
    qc.invalidateQueries({ queryKey: ['posItems'] });
    qc.invalidateQueries({ queryKey: ['posCustomers'] });
    qc.invalidateQueries({ queryKey: ['posCategories'] });
    qc.invalidateQueries({ queryKey: ['items'] });
    qc.invalidateQueries({ queryKey: ['customers'] });
    qc.invalidateQueries({ queryKey: ['suppliers'] });
  };

  const downloadCatalog = async () => {
    setBootstrapping(true);
    try {
      const r = await runChunkedCatalogSync({ force: true });
      invalidateCatalogQueries();
      toast.success(`Local catalog ready: ${r.itemCount} items, ${r.customerCount} customers`);
    } catch (e) {
      toast.error((e as Error).message || 'Catalog sync failed');
    } finally {
      setBootstrapping(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      const result = await syncNow();
      refreshLists();
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customerInvoices'] });
      qc.invalidateQueries({ queryKey: ['partyLedger'] });
      qc.invalidateQueries({ queryKey: ['dailySummary'] });
      if (result.failed > 0) {
        toast.error(`Synced ${result.done}, ${result.failed} still failed — see errors below`);
      } else if (result.done > 0) {
        toast.success(`Synced ${result.done} item(s)`);
      } else {
        toast.success('Nothing to sync');
      }
    } catch (e) {
      toast.error((e as Error).message || 'Sync failed');
    }
  };

  const handleDiscardQueue = async () => {
    const total = pending + failed;
    if (!total) return;
    if (
      !window.confirm(
        `Discard ${total} queue item(s) (${pending} pending, ${failed} failed)?\n\nLocal sales stay on this device but will not upload. You can then switch to Live Online.`,
      )
    ) {
      return;
    }
    const n = await discardAllQueueItems();
    refreshLists();
    toast.success(`Discarded ${n} queue item(s)`);
  };

  const invoiceLabel = (item: SyncQueueItem) => {
    const inv = item.payload?.invoice as { offline_invoice_no?: string } | undefined;
    return inv?.offline_invoice_no || item.clientOfflineId;
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-sm font-medium text-gray-900">Offline sync queue</p>
          <p className="text-xs text-gray-500">
            {pending} pending{failed ? ` · ${failed} failed` : ''}
            {catalogSyncedAt ? ` · catalog ${new Date(catalogSyncedAt).toLocaleString()}` : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            disabled={bootstrapping || catalogSyncOpen || !reachable}
            onClick={() => void downloadCatalog()}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border bg-white hover:bg-gray-100 disabled:opacity-50"
          >
            {bootstrapping || catalogSyncOpen ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Refresh Local Catalog
          </button>
          {(pending > 0 || failed > 0) && (
            <button
              type="button"
              disabled={isSyncing}
              onClick={() => void handleDiscardQueue()}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              <Trash2 className="w-3 h-3" />
              Discard Queue
            </button>
          )}
          {isSyncing ? (
            <button
              type="button"
              onClick={() => requestSyncPause()}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border bg-white"
            >
              Pause
            </button>
          ) : (
            <button
              type="button"
              disabled={!reachable || (pending === 0 && failed === 0)}
              onClick={() => void handleSyncNow()}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-primary-300 bg-primary-50 text-primary-800 disabled:opacity-50"
            >
              <RefreshCw className="w-3 h-3" />
              Sync Now
            </button>
          )}
        </div>
      </div>
      {isSyncing && progress && (
        <p className="text-xs text-primary-700">
          Syncing {progress.done} / {progress.total}
          {progress.failed ? ` · failed ${progress.failed}` : ''}
          {progress.paused ? ' (paused)' : ''}
        </p>
      )}
      {lastSyncError && !isSyncing && (
        <p className="text-xs text-red-600">Last error: {lastSyncError}</p>
      )}
      {Object.keys(stats).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats).map(([type, count]) => (
            <span key={type} className="text-[11px] px-2 py-0.5 rounded bg-white border text-gray-600">
              {type}: {count}
            </span>
          ))}
        </div>
      )}
      {pendingItems.length > 0 && (
        <div className="space-y-1.5 max-h-32 overflow-y-auto">
          <p className="text-[11px] font-medium text-gray-700">Pending items</p>
          {pendingItems.map((item) => (
            <div key={item.clientOfflineId} className="text-[11px] rounded border border-amber-100 bg-white px-2 py-1.5">
              <span className="font-medium text-gray-800">{item.type}</span>
              <span className="text-gray-500"> · {invoiceLabel(item)}</span>
              <span className="text-amber-700"> · {item.status}</span>
            </div>
          ))}
        </div>
      )}
      {failedItems.length > 0 && (
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          <p className="text-[11px] font-medium text-gray-700">Failed items</p>
          {failedItems.map((item) => (
            <div key={item.clientOfflineId} className="text-[11px] rounded border border-red-100 bg-white px-2 py-1.5">
              <span className="font-medium text-gray-800">{item.type}</span>
              <span className="text-gray-500"> · {invoiceLabel(item)}</span>
              {item.error ? <p className="text-red-600 mt-0.5 break-words">{item.error}</p> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
